/** Lead list row labels (All / Account tabs). Keeps `leadName` in API; UI-only helpers. */

export type LeadListContactRef = {
  fullName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
};

export type LeadListLabelInput = {
  leadName?: string | null;
  companyName?: string | null;
  contacts?: LeadListContactRef[] | null;
};

function stripTrailingLeadSuffix(name: string): string {
  return name.replace(/\s+Lead\s*$/i, "").trim();
}

/** True when leadName repeats companyName (often with a trailing " Lead"). */
export function leadNameRedundantWithCompany(
  leadName?: string | null,
  companyName?: string | null,
): boolean {
  const company = companyName?.trim();
  const lead = leadName?.trim();
  if (!company || !lead) return false;
  const companyLower = company.toLowerCase();
  const leadLower = lead.toLowerCase();
  if (leadLower === companyLower) return true;
  if (leadLower === `${companyLower} lead`) return true;
  if (stripTrailingLeadSuffix(lead).toLowerCase() === companyLower) return true;
  return false;
}

export function leadListPrimaryContactName(contacts?: LeadListContactRef[] | null): string | null {
  const contact = contacts?.[0];
  if (!contact) return null;
  return (
    contact.fullName?.trim() ||
    [contact.firstName, contact.lastName].filter(Boolean).join(" ").trim() ||
    contact.email?.trim() ||
    null
  );
}

/** All tab: link text is account name when present, else primary contact, else stripped leadName. */
export function leadListAllTabPrimaryLabel(lead: LeadListLabelInput): string {
  const company = lead.companyName?.trim();
  if (company) return company;
  const contactName = leadListPrimaryContactName(lead.contacts);
  if (contactName) return contactName;
  const leadName = lead.leadName?.trim();
  if (leadName) return stripTrailingLeadSuffix(leadName) || leadName;
  return "Lead";
}

/** All tab: muted line when the primary label is not the account field. */
export function leadListAllTabSubtitle(lead: LeadListLabelInput): string | null {
  if (lead.companyName?.trim()) return null;
  return "No account linked";
}

export type LeadAccountTabLabel = {
  primary: string;
  subtitle: string | null;
};

/** Account tab: show account name only; never stack leadName over company. */
export function leadAccountTabListLabel(lead: LeadListLabelInput): LeadAccountTabLabel {
  const company = lead.companyName?.trim();
  if (company) {
    return { primary: company, subtitle: null };
  }

  const leadName = lead.leadName?.trim();
  if (leadName) {
    const primary = stripTrailingLeadSuffix(leadName) || leadName;
    return { primary, subtitle: "No account linked" };
  }

  return { primary: "No account linked", subtitle: null };
}

/** @deprecated Use leadAccountTabListLabel */
export const leadCompanyTabListLabel = leadAccountTabListLabel;
